import React, { useState } from "react";
import moment from "moment-timezone";
import {
  Box,
  Group,
  Stack,
  Text,
  Textarea,
  Button,
  ActionIcon,
  Badge,
  Tooltip,
} from "@mantine/core";
import { DateTimePicker } from "@mantine/dates";
import { IconSend, IconPencil, IconTrash } from "@tabler/icons-react";
import { useMessaging } from "../../context/MessagingContext";
import DeleteConfirmationModal from "../common/DeleteConfirmationModal";
import type { ScheduledMessage } from "../../types/messaging";
import styles from "./ScheduledPanel.module.css";

interface ScheduledRowProps {
  item: ScheduledMessage;
  timezone: string;
  /** Refresh the parent list after any mutation. */
  onChanged: () => void;
}

// Mantine 8 dates use wall-clock strings ("YYYY-MM-DD HH:mm:ss"); we interpret
// them in the salon's timezone and convert to a UTC ISO for the API.
const WALL = "YYYY-MM-DD HH:mm:ss";

export const ScheduledRow: React.FC<ScheduledRowProps> = ({
  item,
  timezone,
  onChanged,
}) => {
  const { updateScheduled, cancelScheduled, sendScheduledNow } = useMessaging();

  const [editing, setEditing] = useState(false);
  const [editBody, setEditBody] = useState(item.body ?? "");
  const [editWhen, setEditWhen] = useState<string | null>(
    moment.tz(item.scheduled_at, timezone).format(WALL),
  );
  const [busy, setBusy] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const tzAbbr = moment.tz(timezone).format("z");
  const displayTime = moment
    .tz(item.scheduled_at, timezone)
    .format("MMM D, YYYY · h:mm A z");

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    if (!editBody.trim() || !editWhen) return;
    const patch: { body?: string; scheduled_at?: string } = {};
    if (editBody.trim() !== (item.body ?? "")) patch.body = editBody.trim();
    const iso = moment.tz(editWhen, timezone).toISOString();
    if (iso !== item.scheduled_at) patch.scheduled_at = iso;
    if (!patch.body && !patch.scheduled_at) {
      setEditing(false);
      return;
    }
    await run(async () => {
      await updateScheduled(item.scheduled_id, patch);
      setEditing(false);
    });
  };

  if (editing) {
    return (
      <Box className={styles.row}>
        <Stack gap="xs">
          <Textarea
            value={editBody}
            onChange={(e) => setEditBody(e.currentTarget.value)}
            autosize
            minRows={2}
            aria-label="Scheduled message body"
          />
          <DateTimePicker
            value={editWhen}
            onChange={setEditWhen}
            minDate={moment.tz(timezone).format(WALL)}
            valueFormat="MMM D, YYYY h:mm A"
            timePickerProps={{ format: "12h" }}
            label={`Send time (${tzAbbr})`}
          />
          <Group justify="flex-end" gap="xs">
            <Button
              variant="default"
              size="xs"
              onClick={() => setEditing(false)}
            >
              Cancel
            </Button>
            <Button
              size="xs"
              color="teal"
              loading={busy}
              disabled={!editBody.trim() || !editWhen}
              onClick={saveEdit}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Box>
    );
  }

  return (
    <Box className={styles.row}>
      <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
        <Box className={styles.rowText}>
          <Text size="sm" lineClamp={3}>
            {item.body || "(no text)"}
          </Text>
          <Group gap={6} mt={4}>
            <Badge size="xs" variant="light" color="teal">
              {displayTime}
            </Badge>
            {item.status === "CHANGED" && (
              <Badge size="xs" variant="light" color="gray">
                Rescheduled
              </Badge>
            )}
          </Group>
        </Box>
        <Group gap={4} wrap="nowrap">
          <Tooltip label="Send now" withArrow>
            <ActionIcon
              variant="subtle"
              color="teal"
              aria-label="Send now"
              loading={busy}
              onClick={() => run(() => sendScheduledNow(item.scheduled_id))}
            >
              <IconSend size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Edit" withArrow>
            <ActionIcon
              variant="subtle"
              color="gray"
              aria-label="Edit scheduled message"
              onClick={() => setEditing(true)}
            >
              <IconPencil size={16} />
            </ActionIcon>
          </Tooltip>
          <Tooltip label="Cancel" withArrow>
            <ActionIcon
              variant="subtle"
              color="red"
              aria-label="Cancel scheduled message"
              onClick={() => setCancelOpen(true)}
            >
              <IconTrash size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Group>

      <DeleteConfirmationModal
        isOpen={cancelOpen}
        onClose={() => setCancelOpen(false)}
        onConfirm={() =>
          run(async () => {
            await cancelScheduled(item.scheduled_id);
            setCancelOpen(false);
          })
        }
        isLoading={busy}
        title="Cancel scheduled message"
        message="Cancel this scheduled message? It will not be sent."
        confirmLabel="Cancel send"
      />
    </Box>
  );
};

export default ScheduledRow;
